import { useMemo, useRef, type ComponentPropsWithoutRef } from 'react';
import { CheckIcon, ChevronsUpDownIcon, Loader2Icon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { Option } from '@r/types/index.ts';
import { cn } from '@r/utils/utils.ts';
import { Badge } from '@r/components/ui/badge.tsx';
import { Button } from '@r/components/ui/button.tsx';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from '@r/components/ui/command.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@r/components/ui/popover.tsx';

export type MultiSelectOption = Option & {
  description?: string;
  disabled?: boolean;
};

type MultiSelectProps = Omit<ComponentPropsWithoutRef<typeof Button>, 'value' | 'onChange'> & {
  options: MultiSelectOption[];
  value: string[];
  onValueChange: (value: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  emptyMessage?: string;
  loading?: boolean;
  maxSelected?: number;
  maxSelectedMessage?: string;
};

export function MultiSelect({
  options,
  value,
  onValueChange,
  placeholder,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  emptyMessage,
  loading = false,
  maxSelected,
  maxSelectedMessage,
  disabled,
  className,
  ...props
}: MultiSelectProps) {
  const { t } = useTranslation();
  const optionCacheRef = useRef(new Map<string, MultiSelectOption>());

  for (const option of options) {
    optionCacheRef.current.set(option.value, option);
  }

  const selectedValues = new Set(value);
  const selectedOptions = useMemo(
    () =>
      value.map(
        (selectedValue) =>
          optionCacheRef.current.get(selectedValue) ?? {
            label: selectedValue,
            value: selectedValue
          }
      ),
    [value]
  );
  const hasReachedLimit = maxSelected !== undefined && selectedValues.size >= maxSelected;
  const resolvedEmptyMessage = emptyMessage ?? t('common.no.results.found');

  const toggleOption = (option: MultiSelectOption) => {
    const isSelected = selectedValues.has(option.value);

    if (!isSelected && hasReachedLimit) return;

    const nextValues = isSelected
      ? value.filter((selectedValue) => selectedValue !== option.value)
      : [...value, option.value];

    onValueChange(nextValues);
  };

  const clearSelection = () => {
    onValueChange([]);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'h-auto min-h-10 w-full justify-between px-3 py-2 font-normal',
            selectedOptions.length === 0 ? 'text-muted-foreground' : 'text-foreground',
            className
          )}
          {...props}
        >
          <span className="flex flex-wrap gap-1 min-w-0">
            {selectedOptions.length > 0
              ? selectedOptions.map((option) => (
                  <Badge
                    key={option.value}
                    variant="secondary"
                    className="max-w-[12rem] rounded-sm px-1.5 font-normal"
                  >
                    <span className="truncate">{option.label}</span>
                  </Badge>
                ))
              : (placeholder ?? t('common.select'))}
          </span>
          <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        onWheel={(event) => event.stopPropagation()}
        onTouchMove={(event) => event.stopPropagation()}
      >
        <Command>
          <CommandInput
            placeholder={searchPlaceholder ?? placeholder}
            value={searchValue}
            onValueChange={onSearchChange}
          />
          <CommandList className="overscroll-contain">
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2Icon className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : null}
            {!loading ? <CommandEmpty>{resolvedEmptyMessage}</CommandEmpty> : null}
            {!loading ? (
              <CommandGroup>
                {options.map((option) => {
                  const isSelected = selectedValues.has(option.value);
                  const isDisabled = option.disabled || (!isSelected && hasReachedLimit);

                  return (
                    <CommandItem
                      key={option.value}
                      value={`${option.label} ${option.description ?? ''}`}
                      disabled={isDisabled}
                      onSelect={() => toggleOption(option)}
                    >
                      <div
                        className={cn(
                          'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                          isSelected ? 'bg-primary text-primary-foreground' : 'opacity-50'
                        )}
                      >
                        {isSelected ? <CheckIcon className="h-3 w-3" aria-hidden="true" /> : null}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate">{option.label}</p>
                        {option.description ? (
                          <p className="truncate text-xs text-muted-foreground">
                            {option.description}
                          </p>
                        ) : null}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ) : null}
            {maxSelectedMessage && hasReachedLimit ? (
              <p className="px-3 py-2 text-xs text-muted-foreground">{maxSelectedMessage}</p>
            ) : null}
            {selectedValues.size > 0 ? (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem onSelect={clearSelection} className="justify-center text-center">
                    <XIcon className="mr-2 h-4 w-4" />
                    {t('table.clear.filters')}
                  </CommandItem>
                </CommandGroup>
              </>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
